import React, { useState, useEffect } from 'react';

import { PaginationProps, Pagination } from 'main/components';
import { PageNumberContextProps } from 'modules/blog';

export const usePagination = <T extends any>(
  unpagedItems: T[],
  itemsPerPage = 20,
  pageNumber = 1,
  pageNumberContext: PageNumberContextProps,
): [T[], () => JSX.Element] => {
  const [currentPage, setCurrentPage] = useState(
    pageNumberContext.value ? pageNumberContext.value : pageNumber,
  );

  /**
   * Logic for reseting current page on 1 when filter changes
   */
  useEffect(() => {
    if (pageNumberContext.value === 1) {
      setCurrentPage(1);
    }
  }, [pageNumberContext.value]);
  /**
   * Set total page count and ceil to higher integer number
   */
  const totalPages = Math.ceil(unpagedItems.length / itemsPerPage) || 1;

  /**
   * Logic for calculating the amount of returned items per page
   */
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = currentPage * itemsPerPage;
  const pagedItems = unpagedItems.slice(startIndex, endIndex);

  /**
   * Pagination handlers
   */
  const onGoToFirstPage = () => {
    setCurrentPage(1);
    pageNumberContext.dispatch(1);
  };
  const onGoToLastPage = () => {
    pageNumberContext.dispatch(totalPages);
    setCurrentPage(totalPages);
  };
  const onGoToNextPage = () => {
    pageNumberContext.dispatch(currentPage + 1);
    setCurrentPage(currentPage + 1);
  };
  const onGoToPreviousPage = () => {
    pageNumberContext.dispatch(currentPage - 1);
    setCurrentPage(currentPage - 1);
  };
  const onGoToPage = (event: React.MouseEvent<HTMLButtonElement>) => {
    const { pageNumber } = event.currentTarget.dataset;
    if (pageNumber) {
      pageNumberContext.dispatch(parseInt(pageNumber));
      setCurrentPage(parseInt(pageNumber));
    }
  };

  /**
   * Generate Props for pagination component
   */
  const paginationProps: PaginationProps = {
    totalPages,
    currentPage,
    onGoToFirstPage,
    onGoToLastPage,
    onGoToNextPage,
    onGoToPage,
    onGoToPreviousPage,
  };

  const paginationComponent = () => <Pagination {...paginationProps} />;

  return [pagedItems, paginationComponent];
};
